import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PortalMessages = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [filters, setFilters] = useState({
        unread: '',
        client: ''
    });
    const [replyForm, setReplyForm] = useState({
        subject: '',
        body: '',
        priority: 'NORMAL'
    });

    useEffect(() => {
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('access_token');
            let url = '/api/portal/manage/messages/';

            const params = new URLSearchParams();
            if (filters.unread) params.append('unread', filters.unread);
            if (filters.client) params.append('client', filters.client);
            if (params.toString()) url += '?' + params.toString();

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setMessages(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching messages:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            const token = localStorage.getItem('access_token');
            await fetch(`/api/portal/manage/messages/${id}/mark_read/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchMessages();
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!selectedMessage) return;

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/portal/manage/messages/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    client: selectedMessage.client,
                    parent_message: selectedMessage.id,
                    subject: replyForm.subject || `Re: ${selectedMessage.subject}`,
                    body: replyForm.body,
                    priority: replyForm.priority
                })
            });

            if (!response.ok) throw new Error('Failed to send reply');

            setShowReplyModal(false);
            setReplyForm({ subject: '', body: '', priority: 'NORMAL' });
            fetchMessages();
        } catch (err) {
            alert(err.message);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'URGENT': return 'bg-red-100 text-red-800';
            case 'HIGH': return 'bg-orange-100 text-orange-800';
            case 'NORMAL': return 'bg-blue-100 text-blue-800';
            case 'LOW': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <nav className="text-sm text-gray-500 mb-2">
                        <Link to="/portal" className="hover:text-blue-600">Client Portal</Link>
                        <span className="mx-2">›</span>
                        <span>Messages</span>
                    </nav>
                    <h1 className="text-2xl font-bold text-gray-900">Portal Messages</h1>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <select
                            value={filters.unread}
                            onChange={(e) => setFilters({ ...filters, unread: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        >
                            <option value="">All Messages</option>
                            <option value="true">Unread Only</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={fetchMessages}
                            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Messages List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="divide-y divide-gray-100">
                    {messages.length > 0 ? (
                        messages.map((message) => (
                            <div
                                key={message.id}
                                className={`p-4 hover:bg-gray-50 cursor-pointer ${!message.is_read && message.is_from_client ? 'bg-blue-50' : ''}`}
                                onClick={() => setSelectedMessage(message)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            {!message.is_read && message.is_from_client && (
                                                <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                                            )}
                                            <span className={`text-sm ${message.is_from_client ? 'text-green-600' : 'text-blue-600'}`}>
                                                {message.is_from_client ? '← From Client' : '→ To Client'}
                                            </span>
                                            <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(message.priority)}`}>
                                                {message.priority}
                                            </span>
                                        </div>
                                        <h3 className="font-medium text-gray-900">{message.subject}</h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {message.client_name} • {new Date(message.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        {message.is_from_client && !message.is_read && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMarkRead(message.id);
                                                }}
                                                className="text-sm text-blue-600 hover:text-blue-700"
                                            >
                                                Mark Read
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedMessage(message);
                                                setReplyForm({
                                                    subject: `Re: ${message.subject}`,
                                                    body: '',
                                                    priority: 'NORMAL'
                                                });
                                                setShowReplyModal(true);
                                            }}
                                            className="text-sm text-green-600 hover:text-green-700"
                                        >
                                            Reply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="p-12 text-center text-gray-500">
                            No messages found
                        </div>
                    )}
                </div>
            </div>

            {/* Reply Modal */}
            {showReplyModal && selectedMessage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold text-gray-900">Reply to Message</h2>
                                <button onClick={() => setShowReplyModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                Replying to: {selectedMessage.client_name}
                            </p>
                        </div>

                        <form onSubmit={handleReply} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={replyForm.subject}
                                    onChange={(e) => setReplyForm({ ...replyForm, subject: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                                <textarea
                                    required
                                    value={replyForm.body}
                                    onChange={(e) => setReplyForm({ ...replyForm, body: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="5"
                                ></textarea>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                <select
                                    value={replyForm.priority}
                                    onChange={(e) => setReplyForm({ ...replyForm, priority: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="LOW">Low</option>
                                    <option value="NORMAL">Normal</option>
                                    <option value="HIGH">High</option>
                                    <option value="URGENT">Urgent</option>
                                </select>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowReplyModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Send Reply
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortalMessages;
